import { useState, useMemo } from 'react';
import { UserGroup, SyncedGroup, SyncedUser, SyncCategory } from '../../types';
import { Search, Users, ShieldCheck, Mail, Clock, AlertTriangle, Plus } from 'lucide-react';
import { formatSyncTime } from '../../hooks/useClientSyncData';
import HelpTooltip from '../common/HelpTooltip';
import GroupMemberModal from './GroupMemberModal';
import CreateGroupModal from './CreateGroupModal';
import CreateDistributionListModal from './CreateDistributionListModal';
import { useGroups } from '../../hooks/useGroups';

interface GroupListProps {
  clientId: string;
  // Cached sync data passed from parent (preferred source)
  syncData?: SyncCategory<SyncedGroup>;
  // Cached users for the "Add User" picker in GroupMemberModal
  cachedUsers?: SyncedUser[];
  onRefreshNeeded?: () => void;
}

// Derive groupType string from SyncedGroup fields
function deriveGroupType(group: SyncedGroup): string {
  if (group.groupTypes?.includes('Unified') || group.groupTypes?.includes('Microsoft 365')) {
    return 'Microsoft 365';
  }
  if (group.securityEnabled) return 'Security';
  return 'Distribution';
}

// Convert SyncedGroup to UserGroup for compatibility with GroupMemberModal
function syncedToUserGroup(group: SyncedGroup): UserGroup {
  return {
    id: group.id,
    displayName: group.displayName,
    description: group.description,
    groupType: deriveGroupType(group),
    mailEnabled: group.mailEnabled,
    securityEnabled: group.securityEnabled,
    mail: group.mail,
  };
}

// Get badge color based on group type
function getGroupTypeBadge(groupType: string) {
  switch (groupType) {
    case 'Microsoft 365':
      return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200';
    case 'Security':
      return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
    case 'Distribution':
      return 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200';
    default:
      return 'bg-muted text-foreground';
  }
}

// Get icon for group type
function getGroupIcon(groupType: string) {
  switch (groupType) {
    case 'Security':
      return <ShieldCheck className="h-5 w-5 text-green-600 dark:text-green-400" />;
    case 'Distribution':
      return <Mail className="h-5 w-5 text-purple-600 dark:text-purple-400" />;
    default:
      return <Users className="h-5 w-5 text-blue-600 dark:text-blue-400" />;
  }
}

export default function GroupList({ clientId, syncData, cachedUsers, onRefreshNeeded }: GroupListProps) {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTypeFilter, setSelectedTypeFilter] = useState<string>('all');
  const [selectedGroup, setSelectedGroup] = useState<UserGroup | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showCreateDLModal, setShowCreateDLModal] = useState(false);

  // Use cached sync data if available, otherwise fall back to live API
  const hasCachedData = syncData && syncData.status !== 'pending' && (syncData.data?.length > 0 || syncData.status === 'success');
  const {
    groups: liveGroups,
    loading: liveLoading,
    error: liveError,
    refetch: liveRefetch,
  } = useGroups(hasCachedData ? undefined : clientId);

  // Derive display data from cached or live source
  const groups: UserGroup[] = useMemo(() => {
    if (hasCachedData && syncData?.data) {
      return syncData.data.map(syncedToUserGroup);
    }
    return liveGroups;
  }, [hasCachedData, syncData?.data, liveGroups]);

  const loading = hasCachedData ? false : liveLoading;
  const error = hasCachedData
    ? (syncData?.status === 'failed' && syncData.data.length === 0 ? syncData.error : null)
    : liveError;
  const lastSynced = syncData?.lastSynced ?? null;

  // Refresh groups after create/delete
  const handleGroupsChanged = async () => {
    if (hasCachedData && onRefreshNeeded) {
      onRefreshNeeded();
    } else {
      await liveRefetch();
    }
  };

  // Get unique group types for filter dropdown
  const uniqueTypes = useMemo(() => {
    const typeSet = new Set<string>();
    groups.forEach(group => {
      typeSet.add(group.groupType);
    });
    return Array.from(typeSet).sort();
  }, [groups]);

  // Filter groups based on search and type filter
  const filteredGroups = useMemo(() => {
    return groups.filter(group => {
      // Search filter
      const searchLower = searchTerm.toLowerCase();
      const matchesSearch =
        !searchTerm ||
        group.displayName.toLowerCase().includes(searchLower) ||
        (group.description && group.description.toLowerCase().includes(searchLower));

      // Type filter
      const matchesType =
        selectedTypeFilter === 'all' ||
        group.groupType === selectedTypeFilter;

      return matchesSearch && matchesType;
    });
  }, [groups, searchTerm, selectedTypeFilter]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
        <span className="ml-3 text-muted-foreground">Loading groups...</span>
      </div>
    );
  }

  // Never synced and no live data
  if (!hasCachedData && !liveLoading && groups.length === 0 && syncData && syncData.status === 'pending') {
    return (
      <div className="text-center py-12 bg-muted rounded-lg">
        <Clock className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
        <h3 className="text-lg font-medium text-foreground mb-2">
          Data Not Yet Synced
        </h3>
        <p className="text-muted-foreground mb-4">
          Click "Sync Now" above to fetch groups from Microsoft 365.
        </p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
        <div className="flex items-center gap-2">
          <AlertTriangle className="h-5 w-5 text-red-500 flex-shrink-0" />
          <p className="text-red-700 dark:text-red-400">{error}</p>
        </div>
      </div>
    );
  }

  if (groups.length === 0) {
    return (
      <div className="text-center py-12">
        <Users className="h-12 w-12 mx-auto text-muted-foreground" />
        <p className="mt-2 text-muted-foreground">No groups found</p>
        <p className="text-sm text-muted-foreground">
          This client may not have a Microsoft Tenant ID configured
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Staleness indicator */}
      {hasCachedData && lastSynced && (
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <Clock className="h-3.5 w-3.5" />
          <span>Updated {formatSyncTime(lastSynced)}</span>
          {syncData?.status === 'failed' && syncData.data.length > 0 && (
            <span className="text-yellow-600 dark:text-yellow-400 flex items-center gap-1">
              <AlertTriangle className="h-3.5 w-3.5" />
              Last sync had errors
            </span>
          )}
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        {/* Search */}
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
          <input
            type="text"
            placeholder="Search groups..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-border rounded-lg bg-card text-foreground focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          />
        </div>

        {/* Type Filter */}
        <select
          value={selectedTypeFilter}
          onChange={(e) => setSelectedTypeFilter(e.target.value)}
          className="px-4 py-2 border border-border rounded-lg bg-card text-foreground focus:ring-2 focus:ring-primary-500 focus:border-transparent"
        >
          <option value="all">All Types</option>
          {uniqueTypes.map(type => (
            <option key={type} value={type}>{type}</option>
          ))}
        </select>

        {/* Create Group Button */}
        <button
          onClick={() => setShowCreateModal(true)}
          className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors font-medium text-sm"
        >
          <Plus className="h-4 w-4" />
          Create Group
        </button>

        {/* Create Distribution List Button */}
        <button
          onClick={() => setShowCreateDLModal(true)}
          className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors font-medium text-sm"
        >
          <Mail className="h-4 w-4" />
          Create DL
        </button>
      </div>

      {/* Results count */}
      <p className="text-sm text-muted-foreground">
        Showing {filteredGroups.length} of {groups.length} groups
      </p>

      {/* Group List */}
      <div className="bg-card rounded-lg border border-border overflow-hidden">
        <table className="min-w-full divide-y divide-border">
          <thead className="bg-muted">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                Group
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider hidden md:table-cell">
                Description
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                <span className="flex items-center gap-1">
                  Type
                  <HelpTooltip text="Microsoft 365: Teams, shared mailbox, calendar. Security: Control access to resources. Distribution: Email distribution lists." />
                </span>
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider hidden sm:table-cell">
                Members
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider hidden sm:table-cell">
                Properties
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {filteredGroups.map(group => {
              // Look up member count from cached data if available
              const cachedGroup = hasCachedData
                ? syncData?.data.find(g => g.id === group.id)
                : null;

              return (
                <tr
                  key={group.id}
                  onClick={() => setSelectedGroup(group)}
                  className="hover:bg-accent cursor-pointer"
                >
                  <td className="px-4 py-3">
                    <div className="flex items-center">
                      <div className="flex-shrink-0 h-10 w-10 bg-muted rounded-full flex items-center justify-center">
                        {getGroupIcon(group.groupType)}
                      </div>
                      <div className="ml-3">
                        <p className="text-sm font-medium text-foreground">
                          {group.displayName}
                        </p>
                        {group.mail && (
                          <p className="text-xs text-muted-foreground truncate max-w-[200px]" title={group.mail}>
                            {group.mail}
                          </p>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3 hidden md:table-cell">
                    <p className="text-sm text-muted-foreground truncate max-w-xs" title={group.description || ''}>
                      {group.description || <span className="italic text-muted-foreground">No description</span>}
                    </p>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getGroupTypeBadge(group.groupType)}`}>
                      {group.groupType}
                    </span>
                  </td>
                  <td className="px-4 py-3 hidden sm:table-cell">
                    {cachedGroup != null && (
                      <span className="text-sm text-muted-foreground">
                        {cachedGroup.memberCount}
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 hidden sm:table-cell">
                    <div className="flex gap-2">
                      {group.mailEnabled && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-muted text-muted-foreground" title="Mail-enabled">
                          <Mail className="h-3 w-3 mr-1" />
                          Mail
                        </span>
                      )}
                      {group.securityEnabled && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-muted text-muted-foreground" title="Security-enabled">
                          <ShieldCheck className="h-3 w-3 mr-1" />
                          Security
                        </span>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Group Member Modal */}
      {selectedGroup && (
        <GroupMemberModal
          group={selectedGroup}
          clientId={clientId}
          cachedMembers={
            hasCachedData
              ? syncData?.data.find(g => g.id === selectedGroup.id)?.members
              : undefined
          }
          cachedUsers={cachedUsers}
          onClose={() => setSelectedGroup(null)}
          onGroupDeleted={async () => {
            setSelectedGroup(null);
            await handleGroupsChanged();
          }}
        />
      )}

      {/* Create Group Modal */}
      {showCreateModal && (
        <CreateGroupModal
          clientId={clientId}
          onClose={() => setShowCreateModal(false)}
          onCreated={async () => {
            setShowCreateModal(false);
            await handleGroupsChanged();
          }}
        />
      )}

      {/* Create Distribution List Modal */}
      {showCreateDLModal && (
        <CreateDistributionListModal
          clientId={clientId}
          onClose={() => setShowCreateDLModal(false)}
          onCreated={async () => {
            setShowCreateDLModal(false);
            await handleGroupsChanged();
          }}
        />
      )}
    </div>
  );
}
